import { Link } from "react-router-dom";
import { ProgramWatermark } from "../../components/ProgramWatermark";
import { PageHero } from "../../components/PageHero";
import { ScrollReveal } from "../../components/ScrollReveal";
import { Seo } from "../../components/Seo";
import { useLanguage } from "../../i18n/LanguageProvider";

/** The five programs from the revised content document, and how they are delivered. */
export const ServicesPage = () => {
  const { t, tList, path } = useLanguage();
  const serviceKeys = [
    "students",
    "parents",
    "professionals",
    "group",
    "individual",
  ] as const;
  const deliveryKeys = ["OnSite", "Remote", "App"] as const;

  return (
    <>
      <Seo
        titleKey="meta.services.title"
        descriptionKey="meta.services.description"
      />

      <PageHero
        variant="services"
        eyebrow={t("services.eyebrow")}
        title={t("services.title")}
        subtitle={t("services.subtitle")}
      />

      <section className="gw-section">
        <div className="gw-container">
          <div className="gw-stack-lg">
            {serviceKeys.map((key, index) => (
              <ScrollReveal key={key} delay={index * 70}>
                <article
                  className="gw-card gw-card--interactive gw-card--watermarked"
                  style={{ display: "grid", gap: "var(--gw-space-5)" }}
                >
                  <ProgramWatermark program={key} size="lg" />
                  <div
                    className="gw-grid gw-grid--2"
                    style={{ alignItems: "start" }}
                  >
                    <div>
                      <p className="gw-card__index">
                        {t(`services.items.${key}.index`)}
                      </p>
                      <h2
                        style={{
                          fontSize: "var(--gw-text-xl)",
                          marginBlock: "0.4rem 0.75rem",
                        }}
                      >
                        {t(`services.items.${key}.title`)}
                      </h2>
                      <p style={{ color: "var(--gw-ink-muted)" }}>
                        {t(`services.items.${key}.body`)}
                      </p>
                    </div>

                    <div>
                      <p style={{ color: "var(--gw-ink-muted)" }}>
                        {t(`services.items.${key}.detail`)}
                      </p>
                      <ul
                        className="gw-pill-list"
                        style={{ marginTop: "1rem" }}
                      >
                        {tList(`services.items.${key}.points`).map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="gw-delivery">
        <div className="gw-container gw-delivery__inner">
          <h2 className="gw-delivery__title">{t("services.deliveryTitle")}</h2>
          <div className="gw-delivery__grid">
            {deliveryKeys.map((key) => (
              <div key={key} className="gw-delivery__item">
                <div>
                  <p className="gw-delivery__label">
                    {t(`services.delivery${key}Label`)}
                  </p>
                  <p className="gw-delivery__body">
                    {t(`services.delivery${key}Body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gw-section">
        <div className="gw-container">
          <div className="gw-grid gw-grid--2" style={{ alignItems: "start" }}>
            <ScrollReveal direction="left">
              <h2 className="gw-title">{t("app.heading")}</h2>
              <p style={{ color: "var(--gw-ink-muted)" }}>{t("app.body")}</p>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={100}>
              <ul
                className="gw-stack"
                style={{ listStyle: "none", padding: 0 }}
              >
                {(["app.point1", "app.point2", "app.point3"] as const).map(
                  (key) => (
                    <li key={key} className="gw-card">
                      {t(key)}
                    </li>
                  ),
                )}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/*
        The source documents publish no prices, durations, qualifications
        or client references. Rather than inventing a pricing table, the
        page explains how fees are determined and routes to the contact
        form. See docs/open-questions.md Q2.
      */}
      <section className="gw-section gw-section--sunken">
        <div className="gw-container gw-container--narrow">
          <ScrollReveal>
            <h2 className="gw-title">{t("services.pricingNoteHeading")}</h2>
            <p className="gw-lead">{t("services.pricingNoteBody")}</p>
            <p style={{ marginTop: "1.5rem" }}>
              <Link to={path("/contact")} className="gw-btn gw-btn--primary">
                {t("services.cta")}
              </Link>
            </p>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
};
